import { ByAssigneeRoleEnum, PageEditorRoleKeysEnum, PageViewerRoleEnum, RoleEnum } from '@anecdotes/api-contracts';
import { distinct } from '@anecdotes/utils';

export interface PagePermissionMetadata {
  id?: string;
  editRole: string;
  viewRole: PermissionKindMetadata;
  byAssigneeRole?: { role: string; info: string; comingSoon: boolean };
  icon: string;
  onlyFullAccess?: boolean;
  comingSoon?: boolean;
  customRoleToExtend: boolean;
  title: string;
  info?: string;
  pageAccessingPermissions?: string[];
  allRolesToExtend: string[];
  featureFlag?: string;
  hidePermissionRadios?: boolean;
  onlyViewOption: boolean;
}

export interface PermissionKindMetadata {
  role: string,
  comingSoon: boolean,
  info: string,
  viewOnlyText: string,
}

// TODO: Commented entries will be uncomented when they are implemented
const Pages = {
  Dashboard: {
    viewRole: {
      role: PageViewerRoleEnum.ReportingViewerRole,
      comingSoon: false,
      info: null
    },
    editRole: PageEditorRoleKeysEnum.DashboardEditorRole,
    icon: 'dashboard',
    title: 'rolesManagement.pages.dashboard.title',
    customRoleToExtend: true,
    pageAccessingPermissions: ['dashboard:list'],
    hidePermissionRadios: true,
    onlyViewOption: true,
  } as PagePermissionMetadata,
  // TODO: MyAnecdotes is commented for now
  MyAnecdotes: {
    editRole: PageEditorRoleKeysEnum.MyAnecdotesEditorRole,
    viewRole: null,
    icon: 'team-tracker',
    onlyFullAccess: true,
    comingSoon: true,
    customRoleToExtend: true,
    title: 'rolesManagement.pages.myAnecdotes.title',
  } as PagePermissionMetadata,
  EvidenceLab: {
    icon: 'evidence-lab',
    onlyFullAccess: true,
    editRole: PageEditorRoleKeysEnum.EvidenceLabEditorRole,
    customRoleToExtend: true,
    title: 'rolesManagement.pages.evidenceLab.title',
    info: 'rolesManagement.pages.evidenceLab.info',
  } as PagePermissionMetadata,
  Plugin: {
    editRole: PageEditorRoleKeysEnum.PluginsEditorRole,
    icon: 'plugins',
    title: 'rolesManagement.pages.plugins.title',
    pageAccessingPermissions: ['service:write@service:read', RoleEnum.Admin, RoleEnum.It],
    customRoleToExtend: true,
  } as PagePermissionMetadata,
  Policy: {
    editRole: PageEditorRoleKeysEnum.PolicyManagerEditorRole,
    icon: 'policy-nav',
    customRoleToExtend: true,
    title: 'rolesManagement.pages.policies.title',
    pageAccessingPermissions: ['policy:read@policy:write', RoleEnum.Admin, RoleEnum.Collaborator],
  } as PagePermissionMetadata,
  Risk: {
    editRole: PageEditorRoleKeysEnum.RiskManagerEditorRole,
    byAssigneeRole: {
      role: ByAssigneeRoleEnum.RiskByAssignee,
      comingSoon: false,
      info: 'rolesManagement.roleCreation.pagesTab.riskByOwnerTooltip',
    },
    viewRole: {
      role: PageViewerRoleEnum.RiskManageViewerRole,
      comingSoon: false,
      info: null
    },
    icon: 'risk-management',
    title: 'rolesManagement.pages.risks.title',
    customRoleToExtend: true,
    pageAccessingPermissions: ['risk:read@risk:write', RoleEnum.Admin, RoleEnum.Collaborator],
  } as PagePermissionMetadata,
  UAR: {
    editRole: PageEditorRoleKeysEnum.UarEditorRole,
    icon: 'user-access-review',
    title: 'rolesManagement.pages.uar.title',
    customRoleToExtend: true,
    pageAccessingPermissions: ['uar:read@uar:write', RoleEnum.Admin, RoleEnum.Collaborator],
  } as PagePermissionMetadata,
  TrustCenter: {
    editRole: PageEditorRoleKeysEnum.TrustCenterEditor,
    viewRole: {
      role: PageViewerRoleEnum.TrustCenterAccessManager,
      comingSoon: false,
      info: 'rolesManagement.pages.trustCenter.info',
      viewOnlyText: 'rolesManagement.pages.trustCenter.viewOnlyText',
    },
    icon: 'trust-center',
    title: 'rolesManagement.pages.trustCenter.title',
    customRoleToExtend: true,
    pageAccessingPermissions: ['trust-center', RoleEnum.Admin, RoleEnum.TrustCenterAccessManager],
    featureFlag: '@trust-center-tab',
  } as PagePermissionMetadata,
  Playbooks: {
    editRole: PageEditorRoleKeysEnum.PlaybooksEditor,
    icon: 'playbook/play',
    title: 'rolesManagement.pages.playbooks.title',
    customRoleToExtend: true,
    pageAccessingPermissions: [
      'playbook:read@playbook:write',
      // TODO: Roles must be removed when enforcement is done
      RoleEnum.Admin,
      RoleEnum.Collaborator,
      RoleEnum.Manager,
    ],
  } as PagePermissionMetadata,
  Settings: {
    editRole: PageEditorRoleKeysEnum.SettingsEditor,
    icon: 'settings',
    title: 'rolesManagement.pages.settings.title',
    info: 'rolesManagement.pages.settings.info',
    customRoleToExtend: true,
    pageAccessingPermissions: ['customer:update', RoleEnum.Admin, RoleEnum.It],
  } as PagePermissionMetadata,
};

// Automatically assign page id based on property name
Object.entries(Pages).forEach(([key, page]) => {
  (page as PagePermissionMetadata).id = key;
  (page as PagePermissionMetadata).allRolesToExtend = distinct(
    [page.editRole, page.viewRole?.role, page.byAssigneeRole?.role].filter(Boolean),
  );
});

export { Pages };

export const PagesIndexedByRole: Record<string, PagePermissionMetadata> = Object.values(Pages).reduce(
  (result, page: PagePermissionMetadata) => {
    page.allRolesToExtend.forEach((roleToExtend) => {
      result[roleToExtend] = page;
    });

    return result;
  },
  {},
);
